/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import {
  RiTimeLine,
  RiStore2Line,
  RiShoppingCartLine,
  RiShieldCheckLine,
} from "react-icons/ri";
import { FaClock } from "react-icons/fa";
import Context from "../../Context";
import AdminAlertBell from "./AdminAlertBell";

/**
 * Custom Hook: Real-time Dashboard subscription
 */
const useDashboardStats = (db, roundDate) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!db || !roundDate) return;

    try {
      const unsub = onSnapshot(doc(db, "Dashboard", String(roundDate)), (snap) => {
        if (snap.exists()) {
          setStats(snap.data());
        }
      });
      return () => unsub();
    } catch (err) {
      console.warn("Header Dashboard sub error:", err);
    }
  }, [db, roundDate]);

  return stats;
};

/**
 * Isolated Live Digital Clock Component
 * Updates state every 1s without causing parent Header re-renders
 */
const LiveDigitalClock = memo(() => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center gap-1.5 pr-2 shrink-0">
      <RiTimeLine className="text-rose-500 text-xs shrink-0" />
      <span className="font-mono font-bold text-slate-900 text-xs">
        {now.toLocaleTimeString("th-TH")}
      </span>
      <span className="text-[10px] text-slate-400 font-medium">
        ({now.toLocaleDateString("th-TH", { day: "numeric", month: "short" })})
      </span>
    </div>
  );
});

/**
 * Page Title & Icon
 */
const HeaderTitle = memo(({ header, subHeader, icon }) => (
  <div className="flex items-center gap-3">
    <div className="p-2.5 bg-white text-2xl rounded-md shadow-xs border border-slate-200/80 text-rose-600 shrink-0">
      {icon}
    </div>
    <div className="flex flex-col justify-between">
      <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-tight">{header}</h1>
      {subHeader && (
        <span className="text-xs font-semibold text-slate-500 opacity-90">{subHeader}</span>
      )}
    </div>
  </div>
));

/**
 * Round Date and Closing Time Display
 */
const RoundAndCloseInfo = memo(({ roundDate, closeTimeStr }) => (
  <div className="flex items-center gap-2">
    {roundDate && (
      <span className="text-[11px] font-bold text-slate-700 bg-slate-100 border border-slate-200/80 px-1.5 py-0.5 rounded-md shrink-0">
        งวด {roundDate}
      </span>
    )}
    <div className="flex items-center gap-1">
      <FaClock className="text-amber-500 text-[10px] shrink-0" />
      <span className="text-[11px] text-slate-500 font-medium">ปิดขาย:</span>
      <span className="font-mono font-bold text-rose-600 text-xs">
        {closeTimeStr ? `${closeTimeStr} น.` : "ไม่ระบุ"}
      </span>
    </div>
  </div>
));

/**
 * Store and Order Creation Operational Status Badges
 */
const StoreStatusBadges = memo(({ isOpen }) => (
  <div className="flex items-center gap-1.5 pr-2.5 border-r border-slate-200">
    {/* หน้าร้าน */}
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${
        isOpen?.store
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-rose-50 text-rose-700 border-rose-200"
      }`}
      title={isOpen?.store ? "หน้าร้าน: เปิดขาย" : "หน้าร้าน: ปิดชั่วคราว"}
    >
      <RiStore2Line className="text-[10px] shrink-0" />
      <span>หน้าร้าน:</span>
      <span className="font-bold">{isOpen?.store ? "เปิด" : "ปิด"}</span>
    </div>

    {/* รับออเดอร์ */}
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${
        isOpen?.createOrder
          ? "bg-teal-50 text-teal-700 border-teal-200"
          : "bg-rose-50 text-rose-700 border-rose-200"
      }`}
      title={isOpen?.createOrder ? "สร้างคำสั่งซื้อ: เปิด" : "สร้างคำสั่งซื้อ: ปิด"}
    >
      <RiShieldCheckLine className="text-[10px] shrink-0" />
      <span>สร้างคำสั่งซื้อ:</span>
      <span className="font-bold">{isOpen?.createOrder ? "เปิด" : "ปิด"}</span>
    </div>
  </div>
));

/**
 * Real-time Mini KPI Snapshot (Cart, Pending, Paid)
 */
const MiniKpiStats = memo(({ inCartCount, pendingOrdersCount, paidOrdersCount }) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-2">
      {/* ในตะกร้า */}
      <div
        className="flex items-center gap-1 bg-amber-50/90 border border-amber-200/80 px-2 py-0.5 rounded-md text-[11px]"
        title="จำนวนสลากที่ลูกค้ากำลังเลือกใส่ตะกร้า"
      >
        <RiShoppingCartLine className="text-amber-600 text-xs shrink-0" />
        <span className="text-amber-800 font-medium">ตะกร้า:</span>
        <span className="font-mono font-bold text-amber-700">
          {Number(inCartCount).toLocaleString()}
        </span>
        <span className="text-[9px] text-amber-600">ใบ</span>
      </div>

      {/* คำสั่งซื้อรอจ่าย (Clickable -> /orders/pending) */}
      <button
        type="button"
        onClick={() => navigate("/orders/pending")}
        className="flex items-center gap-1 bg-amber-50/90 hover:bg-amber-100 active:scale-95 border border-amber-200/80 px-2 py-0.5 rounded-md text-[11px] cursor-pointer transition-all duration-150"
        title="คลิกเพื่อไปที่หน้ารายการคำสั่งซื้อรอชำระเงิน (/orders/pending)"
      >
        <span className="text-amber-800 font-medium">รอจ่าย:</span>
        <span className="font-mono font-bold text-amber-700">
          {Number(pendingOrdersCount).toLocaleString()}
        </span>
        <span className="text-[9px] text-amber-600">รายการ</span>
      </button>

      {/* คำสั่งซื้อรออนุมัติ (Clickable -> /orders/paid) */}
      <button
        type="button"
        onClick={() => navigate("/orders/paid")}
        className="flex items-center gap-1 bg-rose-50/90 hover:bg-rose-100 active:scale-95 border border-rose-200/80 px-2 py-0.5 rounded-md text-[11px] cursor-pointer transition-all duration-150"
        title="คลิกเพื่อไปที่หน้ารายการคำสั่งซื้อรออนุมัติ (/orders/paid)"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
        <span className="text-rose-800 font-medium">รออนุมัติ:</span>
        <span className="font-mono font-bold text-rose-700">
          {Number(paidOrdersCount).toLocaleString()}
        </span>
        <span className="text-[9px] text-rose-600">รายการ</span>
      </button>
    </div>
  );
});

/**
 * Main Header Component
 */
export const Header = memo(({ header = "", subHeader = "", icon = <></> }) => {
  const { configs, db } = Context();
  const dashboardStats = useDashboardStats(db, configs?.roundDate);

  // Close date-time formatting
  const closeTimeStr = useMemo(() => {
    const rawTime =
      configs?.closeRoundDateTime ||
      configs?.closeCreateOrderDateTime ||
      configs?.closeStoreTime;
    if (!rawTime) return null;
    const d = new Date(rawTime);
    if (isNaN(d.getTime())) return String(rawTime);
    const datePart = d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
    const timePart = d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
    return `${datePart} ${timePart}`;
  }, [configs]);

  // Mini KPI values
  const inCartCount = dashboardStats?.currentLottoInCart ?? 0;
  const pendingOrdersCount =
    dashboardStats?.realtimeOrderStats?.pending?.orders ??
    dashboardStats?.orderPending ??
    0;
  const paidOrdersCount =
    dashboardStats?.realtimeOrderStats?.paid?.orders ??
    dashboardStats?.orderPaid ??
    0;

  return (
    <header className="flex items-center w-full justify-between gap-4 flex-wrap">
      {/* Page Title & Subtitle */}
      <HeaderTitle header={header} subHeader={subHeader} icon={icon} />

      {/* Right Header Widget Area: Live Clock, Close Date-Time, Shop Statuses, Mini KPI & Alert Bell */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Real-time Security Alert Notification Bell */}
        <AdminAlertBell />

        {/* Real-time Operational & KPI 2-Row Capsule */}
        <div className="hidden sm:flex flex-col gap-1.5 bg-white rounded-md p-2 shadow-xs border border-slate-200/80 text-xs">
          {/* Row 1: งวด/วัน-เวลาปิดขาย และ เวลาปัจจุบัน */}
          <div className="flex items-center justify-between gap-3 pb-1 border-b border-slate-100 flex-wrap">
            <RoundAndCloseInfo
              roundDate={configs?.roundDate}
              closeTimeStr={closeTimeStr}
            />
            <LiveDigitalClock />
          </div>

          {/* Row 2: สถานะร้านค้าต่างๆ & Mini KPI Snapshot */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <StoreStatusBadges isOpen={configs?.isOpen} />
            <MiniKpiStats
              inCartCount={inCartCount}
              pendingOrdersCount={pendingOrdersCount}
              paidOrdersCount={paidOrdersCount}
            />
          </div>
        </div>
      </div>
    </header>
  );
});

export default Header;
